import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { PERK_STATE_ACTIVE, PERK_STATE_PENDING } from "constants/perksAdmin";
import { MouseEvent, useMemo } from "react";
import { createColumnHelper } from "react-table-8.10.7";
import ThreeDots from "static/images/ThreeDots.svg";
import { AdminPerk } from "store/apis/perksCrm";
import { TagChip } from "./TagChip";

type UsePerksTableColumnsProps = {
  onManageTags: (perk: AdminPerk) => void;
  onEdit: (perk: AdminPerk) => void;
  onActivate: (perkId: string) => void;
  onDeactivate: (perkId: string) => void;
};

type PerkTagsCellProps = {
  perk: AdminPerk;
  onManageTags: (perk: AdminPerk) => void;
};

const PerkTagsCell = ({ perk, onManageTags }: PerkTagsCellProps) => {
  const handleManageTags = () => onManageTags(perk);
  const tags = perk.tags || [];

  return (
    <div className="t-flex t-flex-wrap t-gap-1 t-items-center">
      {tags.map((tag) => (
        <TagChip key={tag.uuid} tag={tag} />
      ))}
      <Button
        type="button"
        customType="text"
        size="xsmall"
        onClick={handleManageTags}
      >
        {tags.length > 0 ? "edit" : "+ tag"}
      </Button>
    </div>
  );
};

type PerkActionsCellProps = {
  perk: AdminPerk;
  onEdit: (perk: AdminPerk) => void;
  onActivate: (perkId: string) => void;
  onDeactivate: (perkId: string) => void;
};

const PerkActionsCell = ({
  perk,
  onEdit,
  onActivate,
  onDeactivate,
}: PerkActionsCellProps) => {
  const isPending = perk.state === PERK_STATE_PENDING;

  const stopPropagation = (e: MouseEvent) => e.stopPropagation();
  const handleEdit = () => onEdit(perk);
  const handleActivate = () => onActivate(perk.perk_id);
  const handleDeactivate = () => onDeactivate(perk.perk_id);

  return (
    <div onClick={stopPropagation}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <div>
            <Button type="button" size="small" customType="ghost_icon">
              <img src={ThreeDots} alt="Actions" className="t-select-none" />
            </Button>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content sideOffset={8} align="end">
            <Dropdown.Item onSelect={handleEdit}>Edit</Dropdown.Item>
            {isPending ? (
              <Dropdown.Item onSelect={handleActivate}>Activate</Dropdown.Item>
            ) : (
              <Dropdown.Item onSelect={handleDeactivate}>
                Deactivate
              </Dropdown.Item>
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </div>
  );
};

const columnHelper = createColumnHelper<AdminPerk>();

export const usePerksTableColumns = ({
  onManageTags,
  onEdit,
  onActivate,
  onDeactivate,
}: UsePerksTableColumnsProps) =>
  useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "NAME",
        size: 20,
        cell: (info) => (
          <span className="t-text-body-sm t-font-medium t-text-text-100">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor("perk_type", {
        header: "TYPE",
        size: 10,
        cell: (info) => (
          <Tag tagType="purple" size="small" icon={false}>
            {info.getValue()}
          </Tag>
        ),
      }),
      columnHelper.accessor("state", {
        header: "STATUS",
        size: 10,
        cell: (info) => (
          <Tag
            tagType={info.getValue() === PERK_STATE_ACTIVE ? "green" : "yellow"}
            size="small"
            icon={false}
          >
            {info.getValue() === PERK_STATE_ACTIVE ? "Active" : "Pending"}
          </Tag>
        ),
      }),
      columnHelper.accessor("added_by", {
        header: "SUBMITTED BY",
        size: 15,
        cell: (info) => (
          <span className="t-text-body-sm t-text-text-60">
            {info.getValue() || "—"}
          </span>
        ),
      }),
      columnHelper.accessor("created_at", {
        header: "DATE",
        size: 10,
        cell: (info) => (
          <span className="t-text-body-sm t-text-text-30">
            {info.getValue()
              ? dayjs(info.getValue()).format(DISPLAY_DATE)
              : "—"}
          </span>
        ),
      }),
      columnHelper.accessor("tags", {
        header: "TAGS",
        size: 20,
        cell: (info) => (
          <PerkTagsCell perk={info.row.original} onManageTags={onManageTags} />
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        size: 5,
        cell: ({ row }) => (
          <PerkActionsCell
            perk={row.original}
            onEdit={onEdit}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
          />
        ),
      }),
    ],
    [onManageTags, onEdit, onActivate, onDeactivate]
  );
